import {
    ConditionsReport,
    ConditionsReportRequest,
    PowderguideApiInterface
} from './powderguide-api'
import { injectAd, InjectedAd } from './ad-injector'
import { PaginationController } from './pagination-controller'
import { UrlHashHandler } from './url-hash-handler'
import { isEqual } from 'underscore'

// A function rather than a frozen object, so every reset gets fresh arrays.
// The API reads `per_page`; it falls back to 10 when the key is missing.
const defaultRequest = (): ConditionsReportRequest => ({
    page: 1,
    per_page: 24,
    spots: [ ],
    ratings: [ ],
});

export class PowderguideConditionsReports {
    private useHash: boolean = false;
    public loading: boolean = true;
    public error: string|null = null;
    public crs: ConditionsReport[];
    public content: (ConditionsReport|InjectedAd)[];
    public adEvery: number = 12;
    public adOffset: number = 2;
    public readonly pagination: PaginationController = new PaginationController({
        page: 1,
        next_page: 2,
        previous_page: null,
        last_page: 1,
        per_page: 24,
        from_item: 0,
        to_item: 24,
        total_items: 0,
    }, { startFixedElements: 2, endFixedElements: 2, elementsAroundCurrent: 2 }, () => this.loadPage(this.pagination.page));
    public request: ConditionsReportRequest = defaultRequest();
    private hashHandler: UrlHashHandler<[ 'powderguide_crs[page]', 'powderguide_crs[author]', 'powderguide_crs[spot]', 'powderguide_crs[type]', 'powderguide_crs[rating]' ]> = new UrlHashHandler([  'powderguide_crs[page]', 'powderguide_crs[author]', 'powderguide_crs[spot]', 'powderguide_crs[type]', 'powderguide_crs[rating]' ]);
    public authors: { label: string, value: string }[];
    public spots: { label: string, value: string }[];

    constructor(
        private api: PowderguideApiInterface,
        private locale: string,
        Alpine: any
    ) {
        this.crs = Alpine.reactive([ ]);
        this.content = Alpine.reactive([ ]);
        this.authors = Alpine.reactive([ ]);
        this.spots = Alpine.reactive([ ]);
    }


    public async init(config: Partial<{ paginate: number, adEvery: number, adOffset: number, useHash: boolean }> = { }): Promise<void> {
        this.useHash = config.useHash ?? false;
        if (config.paginate) {
            this.pagination.information.per_page = config.paginate;
            this.request.per_page = config.paginate;
        }
        this.adEvery = config.adEvery ?? this.adEvery;
        this.adOffset = config.adOffset ?? this.adOffset;
        if (this.useHash) {
            this.readFromHash();
        }
        await this.updateMeta();

        if (!(await this.updateCrs())) {
            await this.clearFilter();
        }
    }

    public async loadPage(page: number): Promise<void> {
        this.request.page = page;
        await this.updateCrs();
        this.writeToHash();
    }

    private async updateCrs(): Promise<boolean> {
        try {
            this.loading = true;
            this.error = null;
            const result = await this.api.fetchConditionsReports(this.request);
            this.crs.length = 0;
            this.crs.push(...result.conditions_reports);
            this.content.length = 0;
            this.content.push(...injectAd(result.conditions_reports, this.adEvery, this.adOffset));
            this.pagination.update(result.pagination);
            this.request.page = result.pagination.page;
            this.request.per_page = result.pagination.per_page;
            this.writeToHash();
        } catch (e) {
            this.error = e.message;
            return false;
        } finally {
            this.loading = false;
        }

        return true;
    }

    private async updateMeta(): Promise<void> {
        try {
            const result = await this.api.fetchConditionsReportFilterMeta();
            for (const author of result.authors) {
                this.authors.push({ label: author.username, value: author.username });
            }
            this.authors.sort((a, b) => a.label.localeCompare(b.label));

            for (const spot of result.spots) {
                this.spots.push({ label: spot.translations[this.locale]?.title ?? spot.title, value: spot.uuid });
            }
            this.spots.sort((a, b) => a.label.localeCompare(b.label));
        } catch (e) {
            this.error = e.message;
        }
    }

    public get filtersCustomized(): boolean {
        return !!this.request.author
            || !!this.request.type
            || (this.request.spots?.length ?? 0) > 0
            || (this.request.ratings?.length ?? 0) > 0;
    }

    public async clearFilter(): Promise<void> {
        // Keep the page size the block was initialised with.
        this.request = { ...defaultRequest(), per_page: this.request.per_page };
        await this.updateCrs();
        this.writeToHash();
    }

    public async setSpot(spot: string[]|null): Promise<void> {
        await this.updateRequest('spots', spot ?? [ ]);
    }

    public async setType(type: 'mini'|'pro'|null): Promise<void> {
        await this.updateRequest('type', type);
    }

    public async setRating(rating: number[]|null): Promise<void> {
        await this.updateRequest('ratings', rating ?? [ ])
    }

    public async setAuthor(author: string|null): Promise<void> {
        await this.updateRequest('author', author);
    }

    private async updateRequest<TKey extends keyof ConditionsReportRequest>(key: TKey, value: ConditionsReportRequest[TKey]): Promise<void> {
        const changed = !isEqual(this.request[key], value);
        if (!changed) {
            return;
        }

        this.request[key] = value;

        if (key !== 'page') {
            this.request.page = 1;
        }

        if (await this.updateCrs()) {
            this.writeToHash();
        } else {
            // If an error ocurs with the request after changing the filter, reset the filter
            await this.clearFilter();
        }
    }

    private readFromHash(): void {
        const properties = this.hashHandler.read();

        if (properties['powderguide_crs[page]']) {
            this.request.page = parseInt(properties['powderguide_crs[page]']);
        }
        if (properties['powderguide_crs[author]']) {
            this.request.author = properties['powderguide_crs[author]'];
        }
        if (properties['powderguide_crs[spot]']) {
            this.request.spots = properties['powderguide_crs[spot]'].split(',').map(c => c.trim()).filter(c => c.length > 0);
        }
        if (properties['powderguide_crs[type]'] && [ 'mini', 'pro' ].includes(properties['powderguide_crs[type]'])) {
            this.request.type = properties['powderguide_crs[type]'] as 'mini'|'pro';
        }
        if (properties['powderguide_crs[rating]']) {
            this.request.ratings = properties['powderguide_crs[rating]'].split(',').map(c => parseInt(c));
        }
    }

    private writeToHash(): void {
        if (!this.useHash) {
            return;
        }
        this.hashHandler.write({
            'powderguide_crs[page]': this.request.page > 1 ? this.request.page.toString(10) : null,
            'powderguide_crs[author]': this.request.author ?? '',
            'powderguide_crs[spot]': this.request.spots.join(','),
            'powderguide_crs[type]': this.request.type ?? '',
            'powderguide_crs[rating]': this.request.ratings.join(','),
        });
    }
}
